import { supabase } from '../supabase';
import { useOrderStatusHistoryStore } from '../stores/orderStatusHistoryStore';
import { autoSyncService } from './autoSyncService';
import {
  buildCustomFieldsMetaData,
  loadProductCustomFieldDefinitions,
  mergeMetaData,
} from '../../../supabase/functions/_shared/productCustomFields';

export interface WooCommerceConfig {
  site_url: string;
  consumer_key: string;
  consumer_secret: string;
  api_version: string;
}

export interface WooCommerceCategory {
  id?: number;
  name: string;
  slug?: string;
  parent?: number;
  description?: string;
  display?: 'default' | 'products' | 'subcategories' | 'both';
  image?: {
    src: string;
    alt?: string;
  };
  menu_order?: number;
}

export interface WooCommerceProduct {
  id?: number;
  name: string;
  slug?: string;
  type: 'simple' | 'grouped' | 'external' | 'variable';
  status: 'draft' | 'pending' | 'private' | 'publish';
  featured?: boolean;
  catalog_visibility?: 'visible' | 'catalog' | 'search' | 'hidden';
  description?: string;
  short_description?: string;
  sku?: string;
  price?: string;
  regular_price?: string;
  sale_price?: string;
  date_on_sale_from?: string;
  date_on_sale_to?: string;
  price_html?: string;
  on_sale?: boolean;
  purchasable?: boolean;
  total_sales?: number;
  virtual?: boolean;
  downloadable?: boolean;
  downloads?: any[];
  download_limit?: number;
  download_expiry?: number;
  external_url?: string;
  button_text?: string;
  tax_status?: 'taxable' | 'shipping' | 'none';
  tax_class?: string;
  manage_stock?: boolean;
  stock_quantity?: number;
  stock_status?: 'instock' | 'outofstock' | 'onbackorder';
  backorders?: 'no' | 'notify' | 'yes';
  backorders_allowed?: boolean;
  backordered?: boolean;
  low_stock_amount?: number;
  sold_individually?: boolean;
  weight?: string;
  dimensions?: {
    length: string;
    width: string;
    height: string;
  };
  shipping_required?: boolean;
  shipping_taxable?: boolean;
  shipping_class?: string;
  shipping_class_id?: number;
  reviews_allowed?: boolean;
  average_rating?: string;
  rating_count?: number;
  related_ids?: number[];
  upsell_ids?: number[];
  cross_sell_ids?: number[];
  parent_id?: number;
  purchase_note?: string;
  categories?: { id: number; name?: string; slug?: string }[];
  tags?: { id: number; name?: string; slug?: string }[];
  images?: {
    id?: number;
    src: string;
    name?: string;
    alt?: string;
    position?: number;
  }[];
  attributes?: any[];
  default_attributes?: any[];
  variations?: any[];
  grouped_products?: any[];
  menu_order?: number;
  meta_data?: any[];
}

class WooCommerceService {
  private config: WooCommerceConfig | null = null;
  private organizationId: string | null = null;

  /** Solo l'organizzazione (import degli utenti come clienti) quando il negozio e' sul motore: nessuna chiave. */
  setOrganization(organizationId: string): void {
    this.organizationId = organizationId;
    this.config = null;
  }

  async initialize(organizationId: string): Promise<void> {
    this.organizationId = organizationId;
    
    // Get WooCommerce configuration from database
    const { data, error } = await supabase
      .from('api_configurations')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('service_name', 'woocommerce')
      .eq('is_active', true)
      .single();

    if (error || !data) {
      throw new Error('WooCommerce configuration not found. Please configure WooCommerce API settings first.');
    }

    this.config = {
      site_url: data.configuration.site_url,
      consumer_key: data.configuration.consumer_key,
      consumer_secret: data.configuration.consumer_secret,
      api_version: data.configuration.api_version || 'wc/v3'
    };

    // Validate configuration
    if (!this.config.site_url || !this.config.consumer_key || !this.config.consumer_secret) {
      throw new Error('Invalid WooCommerce configuration. Please check your API settings.');
    }
  }

  private getAuthHeaders(): { Authorization: string } {
    if (!this.config) {
      throw new Error('WooCommerce service not initialized');
    }

    const credentials = btoa(`${this.config.consumer_key}:${this.config.consumer_secret}`);
    return {
      Authorization: `Basic ${credentials}`
    };
  }

  private getApiUrl(endpoint: string): string {
    if (!this.config) {
      throw new Error('WooCommerce service not initialized');
    }

    const baseUrl = this.config.site_url.replace(/\/$/, '');
    return `${baseUrl}/wp-json/${this.config.api_version}/${endpoint}`;
  }

  private async makeRequest(
    endpoint: string, 
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', 
    data?: any
  ): Promise<any> {
    const url = this.getApiUrl(endpoint);
    const headers = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders()
    };

    const options: RequestInit = {
      method,
      headers,
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`WooCommerce API error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  // Categories methods
  async getCategories(): Promise<WooCommerceCategory[]> {
    return this.makeRequest('products/categories?per_page=100');
  }

  async createCategory(category: WooCommerceCategory): Promise<WooCommerceCategory> {
    return this.makeRequest('products/categories', 'POST', category);
  }

  async updateCategory(id: number, category: Partial<WooCommerceCategory>): Promise<WooCommerceCategory> {
    return this.makeRequest(`products/categories/${id}`, 'PUT', category);
  }

  async deleteCategory(id: number): Promise<void> {
    await this.makeRequest(`products/categories/${id}`, 'DELETE');
  }

  // Products methods
  async getProducts(): Promise<WooCommerceProduct[]> {
    return this.makeRequest('products?per_page=100');
  }

  /**
   * Fetch ALL WooCommerce products with pagination (for bulk sync).
   * Iterates all pages to ensure no product is missed.
   */
  async getAllProducts(): Promise<WooCommerceProduct[]> {
    const allProducts: WooCommerceProduct[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const url = this.getApiUrl(`products?per_page=100&page=${page}&status=any`);
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`WooCommerce API error (${response.status}): ${errorText}`);
      }

      totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1', 10);
      const products = await response.json();
      allProducts.push(...products);
      page++;
    } while (page <= totalPages);

    console.log(`[WooCommerce] Fetched ${allProducts.length} products (${totalPages} pages)`);
    return allProducts;
  }

  async createProduct(product: WooCommerceProduct): Promise<WooCommerceProduct> {
    return this.makeRequest('products', 'POST', product);
  }

  async updateProduct(id: number, product: Partial<WooCommerceProduct>): Promise<WooCommerceProduct> {
    return this.makeRequest(`products/${id}`, 'PUT', product);
  }

  async deleteProduct(id: number): Promise<void> {
    await this.makeRequest(`products/${id}`, 'DELETE');
  }

  // Sync methods
  async syncCategoriesToWooCommerce(): Promise<{ success: number; errors: string[] }> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    const results = { success: 0, errors: [] as string[] };

    try {
      // Get all categories from CRM
      const { data: crmCategories, error: crmError } = await supabase
        .from('product_categories')
        .select('*')
        .eq('organization_id', this.organizationId)
        .order('name');

      if (crmError) throw crmError;

      // Get existing WooCommerce categories
      const wooCategories = await this.getCategories();
      const wooCaregoryMap = new Map(wooCategories.map(cat => [cat.slug, cat]));

      // Process each CRM category
      for (const crmCategory of crmCategories || []) {
        try {
          const slug = this.generateSlug(crmCategory.name);
          const categoryData: WooCommerceCategory = {
            name: crmCategory.name,
            slug: slug,
            description: crmCategory.description || '',
            display: 'default'
          };

          // Find parent category if exists
          if (crmCategory.parent_id) {
            const { data: parentCategory } = await supabase
              .from('product_categories')
              .select('name')
              .eq('id', crmCategory.parent_id)
              .single();

            if (parentCategory) {
              const parentSlug = this.generateSlug(parentCategory.name);
              const parentWooCategory = wooCaregoryMap.get(parentSlug);
              if (parentWooCategory) {
                categoryData.parent = parentWooCategory.id;
              }
            }
          }

          if (wooCaregoryMap.has(slug)) {
            // Update existing category
            const existingCategory = wooCaregoryMap.get(slug)!;
            await this.updateCategory(existingCategory.id!, categoryData);
          } else {
            // Create new category
            const newCategory = await this.createCategory(categoryData);
            wooCaregoryMap.set(slug, newCategory);
          }

          results.success++;
        } catch (error) {
          results.errors.push(`Error syncing category "${crmCategory.name}": ${(error as Error).message}`);
        }
      }
    } catch (error) {
      results.errors.push(`General error: ${(error as Error).message}`);
    }

    return results;
  }

  async syncProductsToWooCommerce(
    onProgress?: (current: number, total: number, currentProduct: string) => void,
    priceListId?: string // Optional: specify which price list to use instead of default
  ): Promise<{ success: number; errors: string[] }> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    const results = { success: 0, errors: [] as string[] };

    try {
      // Get all products from CRM with relations
      const { data: crmProducts, error: crmError } = await supabase
        .from('products')
        .select(`
          *,
          categories:product_category_relations(
            category:product_categories(*)
          ),
          images:product_images(*),
          files:product_files(*)
        `)
        .eq('organization_id', this.organizationId)
        .order('name');

      // Get ALL price lists and their prices for full sync
      const { data: priceLists } = await supabase
        .from('price_lists')
        .select('*')
        .eq('organization_id', this.organizationId)
        .eq('is_active', true)
        .order('is_default', { ascending: false });

      // Get ALL product prices for all price lists
      const productPrices = new Map();
      if (priceLists && priceLists.length > 0) {
        const priceListIds = priceLists.map(pl => pl.id);
        
        const { data: allPrices } = await supabase
          .from('product_price_list_prices')
          .select(`
            product_id, 
            price_list_id, 
            regular_price, 
            sale_price, 
            sale_price_from, 
            sale_price_to,
            min_quantity,
            max_quantity,
            price_list:price_lists(name, is_default, discount_percentage, currency, is_rental)
          `)
          .in('price_list_id', priceListIds)
          .order('price_list_id')
          .order('min_quantity');
        
        if (allPrices) {
          allPrices.forEach((price: any) => {
            if (!productPrices.has(price.product_id)) {
              productPrices.set(price.product_id, {
                default_price: null,
                price_lists: []
              });
            }

            const productPrice = productPrices.get(price.product_id);
            // price_list can be an object or array depending on Supabase version
            const priceList = Array.isArray(price.price_list) ? price.price_list[0] : price.price_list;

            // Set default price (from default price list with min_quantity = 1)
            if (priceList?.is_default && price.min_quantity === 1 && !productPrice.default_price) {
              productPrice.default_price = {
                regular_price: price.regular_price,
                sale_price: price.sale_price,
                sale_price_from: price.sale_price_from,
                sale_price_to: price.sale_price_to
              };
            }

            // Add to price lists array
            productPrice.price_lists.push({
              price_list_id: price.price_list_id,
              price_list_name: priceList?.name || '',
              is_default: priceList?.is_default || false,
              discount_percentage: priceList?.discount_percentage || 0,
              currency: priceList?.currency || 'EUR',
              is_rental: priceList?.is_rental || false,
              regular_price: price.regular_price,
              sale_price: price.sale_price,
              sale_price_from: price.sale_price_from,
              sale_price_to: price.sale_price_to,
              min_quantity: price.min_quantity,
              max_quantity: price.max_quantity
            });
          });
        }
      }

      if (crmError) throw crmError;

      // Get ALL existing WooCommerce products (paginated) and categories
      const [wooProducts, wooCategories] = await Promise.all([
        this.getAllProducts(),
        this.getCategories()
      ]);

      // Mappa per WC ID (per lookup diretto via woocommerce_product_id)
      const wooProductById = new Map(
        wooProducts.filter(p => p.id).map(prod => [prod.id, prod])
      );

      // Mappa per SKU (fallback)
      const wooProductBySku = new Map(
        wooProducts.filter(p => p.sku).map(prod => [prod.sku, prod])
      );
      const wooCategoryMap = new Map(wooCategories.map(cat => [cat.slug, cat]));

      // Campi prodotto personalizzati dell'org (settings.product_custom_fields)
      const customFieldDefs = await loadProductCustomFieldDefinitions(supabase, this.organizationId);

      const totalProducts = crmProducts?.length || 0;
      
      // Process each CRM product
      for (let i = 0; i < (crmProducts?.length || 0); i++) {
        const crmProduct = crmProducts![i];
        
        // Call progress callback
        if (onProgress) {
          onProgress(i + 1, totalProducts, crmProduct.name);
        }
        
        try {
          const productData: WooCommerceProduct = {
            name: crmProduct.name,
            slug: crmProduct.slug || this.generateSlug(crmProduct.name),
            type: crmProduct.type || 'simple',
            status: crmProduct.status === 'active' ? 'publish' : 'draft',
            featured: crmProduct.featured || false,
            catalog_visibility: crmProduct.visibility || 'visible',
            description: crmProduct.description || '',
            short_description: crmProduct.short_description || '',
            sku: crmProduct.sku || '',
            regular_price: productPrices.get(crmProduct.id)?.default_price?.regular_price?.toString() || crmProduct.regular_price?.toString() || '0',
            sale_price: (() => {
              // 1. Priorità: sale_price dal listino default
              const defaultSalePrice = productPrices.get(crmProduct.id)?.default_price?.sale_price;
              if (defaultSalePrice) return defaultSalePrice.toString();
              // 2. Fallback: sale_price diretto dal prodotto
              if (crmProduct.sale_price && crmProduct.sale_price > 0) return crmProduct.sale_price.toString();
              // 3. Fallback: sconto % dalla prima categoria (per sort_order)
              const regPrice = productPrices.get(crmProduct.id)?.default_price?.regular_price || crmProduct.regular_price || 0;
              const categories = (crmProduct.categories || []).map((r: any) => r.category).filter(Boolean);
              const catWithDiscount = categories
                .filter((c: any) => c.discount_percentage && c.discount_percentage > 0)
                .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))[0];
              if (catWithDiscount && regPrice > 0) {
                const discounted = Math.round(regPrice * (1 - catWithDiscount.discount_percentage / 100) * 100) / 100;
                return discounted.toString();
              }
              return '';
            })(),
            date_on_sale_from: productPrices.get(crmProduct.id)?.default_price?.sale_price_from || '',
            date_on_sale_to: productPrices.get(crmProduct.id)?.default_price?.sale_price_to || '',
            virtual: crmProduct.virtual || false,
            downloadable: crmProduct.downloadable || false,
            tax_status: crmProduct.tax_status || 'taxable',
            tax_class: crmProduct.tax_class || '',
            manage_stock: crmProduct.manage_stock ?? true,
            stock_quantity: crmProduct.stock_quantity || 0,
            stock_status: crmProduct.stock_status || (crmProduct.stock_quantity > 0 ? 'instock' : 'outofstock'),
            backorders: crmProduct.backorders || 'no',
            low_stock_amount: crmProduct.low_stock_threshold || 5,
            weight: crmProduct.weight?.toString() || '',
            dimensions: {
              length: crmProduct.length?.toString() || '',
              width: crmProduct.width?.toString() || '',
              height: crmProduct.height?.toString() || ''
            },
            shipping_class: crmProduct.shipping_class || '',
            purchase_note: crmProduct.purchase_note || '',
            external_url: crmProduct.external_url || '',
            button_text: crmProduct.button_text || '',
            categories: [],
            images: []
          };

          // Map categories
          if (crmProduct.categories) {
            for (const catRel of crmProduct.categories) {
              const categorySlug = this.generateSlug(catRel.category.name);
              const wooCategory = wooCategoryMap.get(categorySlug);
              if (wooCategory) {
                productData.categories!.push({ id: wooCategory.id! });
              }
            }
          }

          // Map images
          if (crmProduct.images) {
            productData.images = crmProduct.images.map((img: any) => ({
              src: img.file_path,
              alt: img.alt_text || '',
              position: img.position || 0
            }));
          }

          // Map downloadable files
          if (crmProduct.files && crmProduct.files.length > 0) {
            const storageBaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const mappedFiles = crmProduct.files
              .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
              .map((file: any) => {
                const fileUrl = file.file_path.startsWith('http')
                  ? file.file_path
                  : `${storageBaseUrl}/storage/v1/object/public/${file.file_path}`;
                return {
                  url: fileUrl,
                  name: file.display_name || file.original_name,
                  original_name: file.original_name
                };
              });
            productData.downloadable = true;
            productData.downloads = mappedFiles.map((f: any) => ({
              name: f.name,
              file: f.url
            }));
            // Store as custom meta for WC plugin frontend display
            if (!productData.meta_data) productData.meta_data = [];
            productData.meta_data.push({
              key: '_product_files',
              value: JSON.stringify(mappedFiles)
            });
          }

          // Add CRM data as meta_data for the WordPress plugin.
          // Conserva le voci gia' aggiunte sopra (_product_files): prima venivano
          // sovrascritte da questa assegnazione e i file non arrivavano al plugin.
          const crmPriceLists = productPrices.get(crmProduct.id)?.price_lists || [];
          productData.meta_data = [
            {
              key: '_crm_product_id',
              value: crmProduct.id
            },
            {
              key: '_crm_sync_timestamp',
              value: new Date().toISOString()
            },
            ...(productData.meta_data || [])
          ];

          // Add barcode if present
          if (crmProduct.barcode) {
            productData.meta_data.push({
              key: '_barcode',
              value: crmProduct.barcode
            });
          }

          // Add video URL (always sync, even when empty to remove from WooCommerce)
          productData.meta_data.push({
            key: '_video_url',
            value: crmProduct.video_url || ''
          });

          // Add cost price (for admin reference only)
          if (crmProduct.cost_price) {
            productData.meta_data.push({
              key: '_cost_price',
              value: crmProduct.cost_price.toString()
            });
          }

          // Add package contents if present
          if (crmProduct.package_contents && crmProduct.package_contents.length > 0) {
            productData.meta_data.push({
              key: '_package_contents',
              value: JSON.stringify(crmProduct.package_contents)
            });
          }

          // Add price lists data
          if (crmPriceLists.length > 0) {
            productData.meta_data.push({
              key: '_crm_price_lists',
              value: JSON.stringify(crmPriceLists)
            });
          }

          // Add category discount info
          const productCategories = (crmProduct.categories || []).map((r: any) => r.category).filter(Boolean);
          const catDiscount = productCategories
            .filter((c: any) => c.discount_percentage && c.discount_percentage > 0)
            .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))[0];
          if (catDiscount) {
            productData.meta_data.push({
              key: '_crm_category_discount',
              value: JSON.stringify({
                category_name: catDiscount.name,
                discount_percentage: catDiscount.discount_percentage
              })
            });
          }

          // Campi personalizzati dell'org: solo le chiavi valorizzate in Flusia
          productData.meta_data = mergeMetaData(
            productData.meta_data,
            buildCustomFieldsMetaData(customFieldDefs, crmProduct.custom_fields)
          );

          // 0. Cerca per woocommerce_product_id salvato (prioritario, autoritativo)
          let existingProduct = crmProduct.woocommerce_product_id
            ? wooProductById.get(crmProduct.woocommerce_product_id)
            : undefined;

          // 1. Fallback: cerca per SKU
          if (!existingProduct) {
            existingProduct = crmProduct.sku ? wooProductBySku.get(crmProduct.sku) : undefined;
          }

          // 3. Update o Create
          if (existingProduct) {
            await this.updateProduct(existingProduct.id!, productData);
          } else {
            await this.createProduct(productData);
          }

          results.success++;
        } catch (error) {
          results.errors.push(`Error syncing product "${crmProduct.name}": ${(error as Error).message}`);
        }
      }
    } catch (error) {
      results.errors.push(`General error: ${(error as Error).message}`);
    }

    return results;
  }

  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Webhook endpoints for receiving WooCommerce data
  async handleOrderCreated(orderData: any): Promise<void> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    try {
      // Process order items to update stock in CRM
      for (const item of orderData.line_items || []) {
        if (item.sku) {
          await this.updateCrmProductStock(item.sku, -item.quantity, {
            reason: 'woocommerce_order',
            order_id: orderData.id,
            order_number: orderData.number,
            customer_email: orderData.billing?.email
          });
        }
      }

      // Optionally create order record in CRM
      await this.createCrmOrder(orderData);
    } catch (error) {
      console.error('Error processing WooCommerce order:', error);
      throw error;
    }
  }

  async handleOrderStatusChanged(orderData: any, oldStatus: string, newStatus: string): Promise<void> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    try {
      // Handle stock adjustments based on status changes
      if (oldStatus === 'processing' && newStatus === 'cancelled') {
        // Restore stock for cancelled orders
        for (const item of orderData.line_items || []) {
          if (item.sku) {
            await this.updateCrmProductStock(item.sku, item.quantity, {
              reason: 'woocommerce_order_cancelled',
              order_id: orderData.id,
              order_number: orderData.number
            });
          }
        }
      }

      // Update order status in CRM if exists
      await this.updateCrmOrderStatus(orderData.id, newStatus);
    } catch (error) {
      console.error('Error handling order status change:', error);
      throw error;
    }
  }

  private async updateCrmProductStock(sku: string, quantityChange: number, metadata: any): Promise<void> {
    try {
      // Find product by SKU
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('id, stock_quantity')
        .eq('organization_id', this.organizationId)
        .eq('sku', sku)
        .single();

      if (productError || !product) {
        console.warn(`Product with SKU ${sku} not found in CRM`);
        return;
      }

      // Update stock quantity
      const newStockQuantity = product.stock_quantity + quantityChange;
      
      const { error: updateError } = await supabase
        .from('products')
        .update({ 
          stock_quantity: Math.max(0, newStockQuantity),
          updated_at: new Date().toISOString()
        })
        .eq('id', product.id);

      if (updateError) throw updateError;

      // Log stock movement
      const previousStock = product.stock_quantity || 0;
      const finalStock = Math.max(0, newStockQuantity);
      const { error: movementError } = await supabase
        .from('stock_movements')
        .insert({
          organization_id: this.organizationId,
          product_id: product.id,
          type: quantityChange > 0 ? 'in' : 'out',
          quantity: Math.abs(quantityChange),
          previous_stock: previousStock,
          new_stock: finalStock,
          reason: metadata.reason,
          notes: `WooCommerce sync - Order ${metadata.order_number}`,
          created_at: new Date().toISOString()
        });

      if (movementError) throw movementError;

    } catch (error) {
      console.error(`Error updating CRM stock for SKU ${sku}:`, error);
      throw error;
    }
  }

  /**
   * Ripristina stock per una lista di item (usato da dialog cancel/refund)
   * Aggiorna sia il CRM che WooCommerce via REST API diretta
   */
  async restoreStockForItems(
    items: Array<{ sku: string; quantity: number; productName: string }>,
    orderId: string,
    orderNumber: string,
    reason: 'order_cancelled' | 'order_refunded'
  ): Promise<{ restored: number; errors: string[] }> {
    const errors: string[] = [];
    let restored = 0;

    for (const item of items) {
      if (!item.sku) {
        errors.push(`${item.productName}: SKU mancante`);
        continue;
      }

      try {
        // 1. Ripristina stock nel CRM
        await this.updateCrmProductStock(item.sku, item.quantity, {
          reason,
          order_id: orderId,
          order_number: orderNumber
        });

        // 2. Leggi la qty aggiornata dal CRM
        const { data: crmProduct } = await supabase
          .from('products')
          .select('id, stock_quantity')
          .eq('organization_id', this.organizationId)
          .eq('sku', item.sku)
          .maybeSingle();

        if (!crmProduct) {
          console.warn(`Product ${item.sku} not found in CRM after stock update`);
          restored++;
          continue;
        }

        // 3. Sincronizza stock a WooCommerce via REST API diretta
        try {
          await this.syncProductStockToWooCommerce(item.sku, crmProduct.stock_quantity || 0);
          console.log(`Stock synced to WooCommerce for ${item.sku}: ${crmProduct.stock_quantity}`);
        } catch (wcErr) {
          console.warn(`WC stock sync failed for ${item.sku}:`, wcErr);
          // Non blocca il ripristino CRM se la sync WC fallisce
        }

        restored++;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        errors.push(`${item.productName}: ${errorMsg}`);
      }
    }

    return { restored, errors };
  }

  /**
   * Sincronizza stock di un prodotto a WooCommerce via REST API diretta
   * Cerca il prodotto WC per SKU e aggiorna la stock_quantity
   */
  private async syncProductStockToWooCommerce(sku: string, newQuantity: number): Promise<void> {
    if (!this.config) {
      throw new Error('WooCommerce service not initialized');
    }

    // Cerca prodotto WC per SKU
    const products = await this.makeRequest(`products?sku=${encodeURIComponent(sku)}&per_page=1`);

    if (products && products.length > 0) {
      const wcProductId = products[0].id;
      await this.makeRequest(`products/${wcProductId}`, 'PUT', {
        stock_quantity: newQuantity,
        manage_stock: true
      });
      return;
    }

    // Prova variazioni: cerca in tutte le variazioni
    // WC REST API non supporta ricerca variazioni per SKU direttamente,
    // quindi cerchiamo tra i prodotti variabili
    console.log(`Product not found by SKU ${sku} as simple product, trying variations...`);

    // Cerca il prodotto parent nel CRM per trovare il wc_product_id
    const { data: crmProduct } = await supabase
      .from('products')
      .select('woocommerce_product_id')
      .eq('organization_id', this.organizationId)
      .eq('sku', sku)
      .maybeSingle();

    if (crmProduct?.woocommerce_product_id) {
      // Prova come variazione del parent
      const variations = await this.makeRequest(
        `products/${crmProduct.woocommerce_product_id}/variations?per_page=100`
      );

      const matchingVariation = variations?.find((v: any) => v.sku === sku);
      if (matchingVariation) {
        await this.makeRequest(
          `products/${crmProduct.woocommerce_product_id}/variations/${matchingVariation.id}`,
          'PUT',
          { stock_quantity: newQuantity, manage_stock: true }
        );
        return;
      }
    }

    console.warn(`Could not find WooCommerce product for SKU: ${sku}`);
  }

  private async createCrmOrder(orderData: any): Promise<void> {
    try {
      // Create order record in CRM (optional - depends on your CRM structure)
      const { error } = await supabase
        .from('woocommerce_orders')
        .insert({
          organization_id: this.organizationId,
          woo_order_id: orderData.id,
          order_number: orderData.number,
          status: orderData.status,
          total: parseFloat(orderData.total),
          currency: orderData.currency,
          customer_email: orderData.billing?.email,
          customer_name: `${orderData.billing?.first_name || ''} ${orderData.billing?.last_name || ''}`.trim(),
          order_data: orderData,
          created_at: new Date().toISOString()
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error creating CRM order:', error);
      // Don't throw - order creation in CRM is optional
    }
  }

  private async updateCrmOrderStatus(wooOrderId: number, newStatus: string): Promise<void> {
    try {
      // Get current order data for status history
      const { data: currentOrder } = await supabase
        .from('woocommerce_orders')
        .select('id, status, order_number')
        .eq('organization_id', this.organizationId)
        .eq('woo_order_id', wooOrderId)
        .single();

      const oldStatus = currentOrder?.status;

      const { error } = await supabase
        .from('woocommerce_orders')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('organization_id', this.organizationId)
        .eq('woo_order_id', wooOrderId);

      if (error && error.code !== 'PGRST116') { // Ignore if record doesn't exist
        throw error;
      }

      // Log status change from webhook
      if (currentOrder && oldStatus !== newStatus) {
        await useOrderStatusHistoryStore.getState().logStatusChange({
          orderId: currentOrder.id,
          channel: 'woocommerce',
          orderNumber: currentOrder.order_number,
          fromStatus: oldStatus,
          toStatus: newStatus,
          source: 'webhook'
        });
      }
    } catch (error) {
      console.error('Error updating CRM order status:', error);
      // Don't throw - this is optional
    }
  }

  // Method to get webhook URL for WordPress plugin configuration
  getWebhookEndpoints(): { order_created: string; order_updated: string } {
    const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'https://your-crm-domain.com';
    return {
      order_created: `${baseUrl}/api/webhooks/woocommerce/order-created`,
      order_updated: `${baseUrl}/api/webhooks/woocommerce/order-updated`
    };
  }

  // Test connection method
  async testConnection(): Promise<boolean> {
    try {
      await this.makeRequest('products/categories?per_page=1');
      return true;
    } catch (error) {
      console.error('WooCommerce connection test failed:', error);
      return false;
    }
  }

  // ========================================
  // Order Management Methods
  // ========================================

  /**
   * Get WooCommerce orders with filters
   */
  async getOrders(filters?: {
    status?: string;
    clientId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<WooCommerceOrderRecord[]> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    let query = supabase
      .from('woocommerce_orders')
      .select(`
        *,
        client:clients(id, business_name, email, vat_number)
      `)
      .eq('organization_id', this.organizationId)
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.clientId) {
      query = query.eq('client_id', filters.clientId);
    }

    if (filters?.search) {
      query = query.or(`order_number.ilike.%${filters.search}%,customer_email.ilike.%${filters.search}%,customer_name.ilike.%${filters.search}%`);
    }

    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, (filters.offset + (filters.limit || 10)) - 1);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data || [];
  }

  /**
   * Get a single order by ID
   */
  async getOrderById(orderId: string): Promise<WooCommerceOrderRecord | null> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    const { data, error } = await supabase
      .from('woocommerce_orders')
      .select(`
        *,
        client:clients(id, business_name, email, vat_number, phone, street_address, city, postal_code, province)
      `)
      .eq('organization_id', this.organizationId)
      .eq('id', orderId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data;
  }

  /**
   * Link an order to a CRM client
   */
  async linkOrderToClient(orderId: string, clientId: string): Promise<void> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    const { error } = await supabase
      .from('woocommerce_orders')
      .update({
        client_id: clientId,
        updated_at: new Date().toISOString()
      })
      .eq('organization_id', this.organizationId)
      .eq('id', orderId);

    if (error) throw error;
  }

  /**
   * Unlink an order from a CRM client
   */
  async unlinkOrderFromClient(orderId: string): Promise<void> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    const { error } = await supabase
      .from('woocommerce_orders')
      .update({
        client_id: null,
        updated_at: new Date().toISOString()
      })
      .eq('organization_id', this.organizationId)
      .eq('id', orderId);

    if (error) throw error;
  }

  /**
   * Auto-match an order to a client by email or VAT number
   */
  async autoMatchOrderToClient(orderId: string): Promise<string | null> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    // Get the order
    const order = await this.getOrderById(orderId);
    if (!order) {
      throw new Error('Order not found');
    }

    const orderData = order.order_data as any;

    // Try to match by email
    if (order.customer_email) {
      const { data: clientByEmail } = await supabase
        .from('clients')
        .select('id')
        .eq('organization_id', this.organizationId)
        .eq('email', order.customer_email.toLowerCase())
        .maybeSingle();

      if (clientByEmail) {
        await this.linkOrderToClient(orderId, clientByEmail.id);
        return clientByEmail.id;
      }
    }

    // Try to match by VAT number from meta_data
    const vatFields = ['billing_vat', '_billing_vat_number', 'vat_number', '_vat_number', 'billing_piva', '_billing_piva'];
    for (const field of vatFields) {
      const vatMeta = orderData?.meta_data?.find((m: any) => m.key === field);
      if (vatMeta?.value) {
        const vatNumber = vatMeta.value.replace(/\s/g, '').toUpperCase();

        const { data: clientByVat } = await supabase
          .from('clients')
          .select('id')
          .eq('organization_id', this.organizationId)
          .eq('vat_number', vatNumber)
          .maybeSingle();

        if (clientByVat) {
          await this.linkOrderToClient(orderId, clientByVat.id);
          return clientByVat.id;
        }

        // Try tax_code field
        const { data: clientByTax } = await supabase
          .from('clients')
          .select('id')
          .eq('organization_id', this.organizationId)
          .eq('tax_code', vatNumber)
          .maybeSingle();

        if (clientByTax) {
          await this.linkOrderToClient(orderId, clientByTax.id);
          return clientByTax.id;
        }
      }
    }

    // Try to match by company name
    const companyName = orderData?.billing?.company;
    if (companyName) {
      const { data: clientByCompany } = await supabase
        .from('clients')
        .select('id')
        .eq('organization_id', this.organizationId)
        .ilike('business_name', companyName.trim())
        .maybeSingle();

      if (clientByCompany) {
        await this.linkOrderToClient(orderId, clientByCompany.id);
        return clientByCompany.id;
      }
    }

    return null;
  }

  /**
   * Update order status in CRM
   */
  async updateOrderStatus(orderId: string, newStatus: string, source: 'manual' | 'webhook' = 'manual', reason?: string): Promise<void> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    // Get current status before update
    const { data: currentOrder, error: fetchError } = await supabase
      .from('woocommerce_orders')
      .select('status, order_number')
      .eq('organization_id', this.organizationId)
      .eq('id', orderId)
      .single();

    if (fetchError) {
      throw new Error(`Order not found: ${fetchError.message}`);
    }

    const oldStatus = currentOrder.status;

    const { error } = await supabase
      .from('woocommerce_orders')
      .update({
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq('organization_id', this.organizationId)
      .eq('id', orderId);

    if (error) throw error;

    // Log status change
    await useOrderStatusHistoryStore.getState().logStatusChange({
      orderId,
      channel: 'woocommerce',
      orderNumber: currentOrder.order_number,
      fromStatus: oldStatus,
      toStatus: newStatus,
      source,
      reason
    });
  }

  /**
   * Mappa stati CRM a stati WooCommerce validi
   * Stati WooCommerce validi: pending, processing, on-hold, completed, cancelled, refunded, failed
   * Stati custom registrati in WooCommerce dal plugin: prepared, shipped
   * NOTA: 'trash' NON è uno stato valido per l'API - va gestito con DELETE
   */
  private mapStatusToWooCommerce(crmStatus: string): string | null {
    const statusMap: Record<string, string | null> = {
      'pending': 'pending',
      'processing': 'processing',
      'on-hold': 'on-hold',
      'completed': 'completed',
      'cancelled': 'cancelled',
      'refunded': 'refunded',
      'failed': 'failed',
      // Stati custom registrati in WooCommerce dal plugin
      'prepared': 'prepared',
      'shipped': 'shipped'
      // 'trash' NON va qui - viene gestito separatamente con DELETE
    };
    return statusMap[crmStatus] ?? null;
  }

  /**
   * Sposta un ordine nel cestino di WooCommerce
   * Usa DELETE con force=false per soft delete (cestino)
   * DELETE con force=true eliminerebbe definitivamente l'ordine
   */
  async trashOrderOnWooCommerce(wooOrderId: number): Promise<boolean> {
    try {
      // DELETE con force=false sposta nel cestino (soft delete)
      await this.makeRequest(`orders/${wooOrderId}?force=false`, 'DELETE');
      return true;
    } catch (error) {
      console.error('Error trashing order on WooCommerce:', error);
      return false;
    }
  }

  /**
   * Sync order status to WooCommerce
   */
  async syncStatusToWooCommerce(orderId: string, newStatus: string): Promise<boolean> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    // Get order with woo_order_id
    const order = await this.getOrderById(orderId);
    if (!order?.woo_order_id) {
      console.warn('Order has no WooCommerce ID, cannot sync');
      return false;
    }

    // Caso speciale: trash usa DELETE invece di PUT
    if (newStatus === 'trash') {
      return this.trashOrderOnWooCommerce(order.woo_order_id);
    }

    // Mappa lo stato CRM a uno stato WooCommerce valido
    const wcStatus = this.mapStatusToWooCommerce(newStatus);
    if (!wcStatus) {
      // Stato interno CRM, non sincronizzare ma consideralo un successo
      console.log(`Status "${newStatus}" is CRM-only, not syncing to WooCommerce`);
      return true;
    }

    try {
      // Call WooCommerce API to update status
      await this.makeRequest(`orders/${order.woo_order_id}`, 'PUT', {
        status: wcStatus
      });

      return true;
    } catch (error) {
      console.error('Error syncing status to WooCommerce:', error);
      return false;
    }
  }

  /**
   * Sync order status to WooCommerce by woo_order_id directly
   */
  async syncStatusToWooCommerceByWooId(wooOrderId: number, newStatus: string): Promise<boolean> {
    // Caso speciale: trash usa DELETE invece di PUT
    if (newStatus === 'trash') {
      return this.trashOrderOnWooCommerce(wooOrderId);
    }

    // Mappa lo stato CRM a uno stato WooCommerce valido
    const wcStatus = this.mapStatusToWooCommerce(newStatus);
    if (!wcStatus) {
      // Stato interno CRM, non sincronizzare ma consideralo un successo
      console.log(`Status "${newStatus}" is CRM-only, not syncing to WooCommerce`);
      return true;
    }

    try {
      await this.makeRequest(`orders/${wooOrderId}`, 'PUT', {
        status: wcStatus
      });
      return true;
    } catch (error) {
      console.error('Error syncing status to WooCommerce:', error);
      return false;
    }
  }

  /**
   * Update order meta_data on WooCommerce (e.g., tracking number)
   */
  async updateOrderMeta(wooOrderId: number, data: {
    meta_data?: Array<{ key: string; value: string }>;
    status?: string;
  }): Promise<boolean> {
    try {
      await this.makeRequest(`orders/${wooOrderId}`, 'PUT', data);
      return true;
    } catch (error) {
      console.error('Error updating order meta on WooCommerce:', error);
      return false;
    }
  }

  /**
   * Update order internal notes
   */
  async updateOrderNotes(orderId: string, notes: string): Promise<void> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    const { error } = await supabase
      .from('woocommerce_orders')
      .update({
        internal_notes: notes,
        notes_updated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('organization_id', this.organizationId)
      .eq('id', orderId);

    if (error) throw error;
  }

  /**
   * Create a CRM client from order billing data
   */
  async createClientFromOrder(orderId: string): Promise<string | null> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    // Get the order
    const order = await this.getOrderById(orderId);
    if (!order) {
      throw new Error('Order not found');
    }

    const orderData = order.order_data as any;
    const billing = orderData?.billing;

    if (!billing) {
      throw new Error('Order has no billing data');
    }

    // Extract VAT number from meta_data
    const vatFields = ['billing_vat', '_billing_vat_number', 'vat_number', '_vat_number', 'billing_piva', '_billing_piva'];
    let vatNumber = null;
    for (const field of vatFields) {
      const vatMeta = orderData?.meta_data?.find((m: any) => m.key === field);
      if (vatMeta?.value) {
        vatNumber = vatMeta.value.replace(/\s/g, '').toUpperCase();
        break;
      }
    }

    // Extract tax code from meta_data
    const taxCodeFields = ['billing_cf', '_billing_cf', 'codice_fiscale', '_codice_fiscale', 'tax_code'];
    let taxCode = null;
    for (const field of taxCodeFields) {
      const taxMeta = orderData?.meta_data?.find((m: any) => m.key === field);
      if (taxMeta?.value) {
        taxCode = taxMeta.value.replace(/\s/g, '').toUpperCase();
        break;
      }
    }

    // Determine client type
    const isCompany = !!billing.company;

    // Prepare client data
    const clientData: any = {
      organization_id: this.organizationId,
      type: isCompany ? 'company' : 'individual',
      business_name: isCompany ? billing.company : `${billing.first_name || ''} ${billing.last_name || ''}`.trim(),
      first_name: billing.first_name || null,
      last_name: billing.last_name || null,
      email: billing.email?.toLowerCase() || null,
      phone: billing.phone || null,
      vat_number: vatNumber,
      tax_code: taxCode,
      street_address: billing.address_1 || null,
      city: billing.city || null,
      province: billing.state || null,
      postal_code: billing.postcode || null,
      country: billing.country || 'IT',
      status: 'active',
      source: 'woocommerce',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Create client
    const { data, error } = await supabase
      .from('clients')
      .insert(clientData)
      .select('id')
      .single();

    if (error) throw error;

    if (data) {
      // Link order to the new client
      await this.linkOrderToClient(orderId, data.id);
      return data.id;
    }

    return null;
  }

  /**
   * Get order statistics
   */
  async getOrderStats(): Promise<{
    total: number;
    pending: number;
    processing: number;
    completed: number;
    cancelled: number;
    linkedToClient: number;
  }> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    const { data, error } = await supabase
      .from('woocommerce_orders')
      .select('status, client_id')
      .eq('organization_id', this.organizationId);

    if (error) throw error;

    const orders = data || [];
    return {
      total: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      processing: orders.filter(o => o.status === 'processing').length,
      completed: orders.filter(o => o.status === 'completed').length,
      cancelled: orders.filter(o => ['cancelled', 'failed', 'refunded'].includes(o.status)).length,
      linkedToClient: orders.filter(o => o.client_id !== null).length,
    };
  }

  // ========================================
  // WooCommerce Users by Price List
  // ========================================

  /**
   * Get custom API URL for plugin endpoints (not WooCommerce REST API)
   */
  private getCustomApiUrl(endpoint: string): string {
    if (!this.config) {
      throw new Error('WooCommerce service not initialized');
    }
    const baseUrl = this.config.site_url.replace(/\/$/, '');
    return `${baseUrl}/wp-json/${endpoint}`;
  }

  /**
   * Get WooCommerce users assigned to a specific price list
   * Calls the custom plugin endpoint /wp-json/aplwc/v1/pricelists/{id}/users
   */
  async getUsersByPriceList(priceListId: string): Promise<WooCommerceUsersResponse> {
    if (!this.organizationId) {
      return { success: false, data: [], total: 0, error: 'Organization ID not set' };
    }

    if (!this.config) {
      return { success: false, data: [], total: 0, error: 'WooCommerce service not initialized' };
    }

    try {
      const url = this.getCustomApiUrl(`aplwc/v1/pricelists/${priceListId}/users`);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          data: [],
          total: 0,
          error: `WooCommerce API error (${response.status}): ${errorText}`
        };
      }

      const result = await response.json();
      return {
        success: result.success ?? true,
        data: result.data || [],
        total: result.total || 0
      };
    } catch (error) {
      console.error('Error fetching WooCommerce users by price list:', error);
      return {
        success: false,
        data: [],
        total: 0,
        error: (error as Error).message
      };
    }
  }

  /**
   * Get ALL approved WooCommerce users (independent of price list)
   * Calls the custom plugin endpoint /wp-json/aplwc/v1/users/approved
   */
  async getAllApprovedUsers(): Promise<WooCommerceUsersResponse> {
    if (!this.organizationId) {
      return { success: false, data: [], total: 0, error: 'Organization ID not set' };
    }

    if (!this.config) {
      return { success: false, data: [], total: 0, error: 'WooCommerce service not initialized' };
    }

    try {
      const url = this.getCustomApiUrl('aplwc/v1/users/approved');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          data: [],
          total: 0,
          error: `WooCommerce API error (${response.status}): ${errorText}`
        };
      }

      const result = await response.json();
      return {
        success: result.success ?? true,
        data: result.data || [],
        total: result.total || 0
      };
    } catch (error) {
      console.error('Error fetching all approved WooCommerce users:', error);
      return {
        success: false,
        data: [],
        total: 0,
        error: (error as Error).message
      };
    }
  }

  /**
   * Import a single WooCommerce user as a CRM client
   * Matches by woocommerce_user_id first, then by email
   *
   * @param user - WooCommerce user data
   * @param assignedTo - UUID of CRM user to assign the client to
   * @param statoCliente - UUID from client_states table (must have is_lead=false)
   */
  async importUserAsClient(
    user: WooCommerceUser,
    assignedTo: string,
    statoCliente: string
  ): Promise<UserImportResult> {
    if (!this.organizationId) {
      throw new Error('Organization ID not set');
    }

    // Skip non-approved users
    if (user.ur_status !== 1) {
      throw new Error(`User ${user.email} is not approved (status: ${user.ur_status})`);
    }

    // 1. Check if client already exists by woocommerce_user_id
    const { data: existingByWooId } = await supabase
      .from('clients')
      .select('id')
      .eq('organization_id', this.organizationId)
      .eq('woocommerce_user_id', user.id)
      .maybeSingle();

    // Build business name
    const businessName = user.billing_company ||
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.display_name ||
      user.email;

    // Prepare client data for insert/update
    // Note: stato_cliente must be UUID from client_states, NOT a string
    const clientData = {
      organization_id: this.organizationId, // Include in updates too!
      business_name: businessName,
      email: user.email?.toLowerCase() || null,
      phone: user.billing_phone || null,
      whatsapp_number: user.billing_phone || null,
      street_address: user.billing_address_1 || null,
      city: user.billing_city || null,
      province: user.billing_state || null,
      postal_code: user.billing_postcode || null,
      country: user.billing_country || 'IT',
      vat_number: user.vat_number || null,
      tax_code: user.fiscal_code || null,
      pec: user.pec || null,
      sdi_code: user.sdi_code || null,
      annual_revenue: user.annual_revenue || null,
      entity_type: user.is_b2b || user.billing_company ? 'company' : 'individual',
      default_price_list_id: user.assigned_price_list_id || null, // Use price list from WooCommerce user
      woocommerce_user_id: user.id,
      assigned_to: assignedTo, // CRM user responsible for this client
      stato_cliente: statoCliente, // UUID from client_states (NOT a string!)
      updated_at: new Date().toISOString()
    };

    if (existingByWooId) {
      // Update existing client (matched by WooCommerce ID)
      // Don't update assigned_to for existing clients - only data fields
      const { assigned_to: _, ...updateData } = clientData;
      const { error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', existingByWooId.id);

      if (error) throw new Error(`Error updating client: ${error.message}`);
      return { clientId: existingByWooId.id, action: 'updated' };
    }

    // 2. Check if client exists by email (fallback matching)
    if (user.email) {
      const { data: existingByEmail } = await supabase
        .from('clients')
        .select('id')
        .eq('organization_id', this.organizationId)
        .eq('email', user.email.toLowerCase())
        .maybeSingle();

      if (existingByEmail) {
        // Link existing client to WooCommerce user and update
        // Don't update assigned_to for existing clients - only data fields
        const { assigned_to: _, ...updateData } = clientData;
        const { error } = await supabase
          .from('clients')
          .update(updateData)
          .eq('id', existingByEmail.id);

        if (error) throw new Error(`Error updating client: ${error.message}`);
        return { clientId: existingByEmail.id, action: 'updated' };
      }
    }

    // 3. Create new client
    const { data: newClient, error: insertError } = await supabase
      .from('clients')
      .insert({
        ...clientData,
        created_at: new Date().toISOString()
      })
      .select('id')
      .single();

    if (insertError) throw new Error(`Error creating client: ${insertError.message}`);

    // Add a note with WooCommerce registration data (tipo_azienda, fatturato)
    await this.addWooCommerceRegistrationNote(newClient.id, user, assignedTo);

    return { clientId: newClient.id, action: 'created' };
  }

  /**
   * Add a note with WooCommerce registration data
   * Saves tipo_azienda and fatturato from the WP registration form
   */
  private async addWooCommerceRegistrationNote(
    clientId: string,
    user: WooCommerceUser,
    createdBy: string
  ): Promise<void> {
    // Only add note if there's data to save
    if (!user.tipo_azienda && !user.fatturato) {
      return;
    }

    const noteParts: string[] = ['Dati da registrazione WooCommerce:'];

    if (user.tipo_azienda) {
      noteParts.push(`- Tipologia Azienda: ${user.tipo_azienda}`);
    }

    if (user.fatturato) {
      noteParts.push(`- Fatturato dichiarato: ${user.fatturato}`);
    }

    const noteContent = noteParts.join('\n');

    try {
      await supabase
        .from('clients')
        .update({ interest_notes: noteContent })
        .eq('id', clientId);
    } catch (error) {
      // Don't fail the import if note creation fails
      console.error('Error saving WooCommerce registration data to interest_notes:', error);
    }
  }

  /**
   * Batch import selected WooCommerce users
   * Used by the new WooCommerceClientsManager component
   *
   * @param users - Array of WooCommerce users to import
   * @param assignedTo - UUID of CRM user to assign the clients to
   * @param statoCliente - UUID from client_states table
   * @param onProgress - Optional progress callback
   */
  async importSelectedUsers(
    users: WooCommerceUser[],
    assignedTo: string,
    statoCliente: string,
    onProgress?: (current: number, total: number, userName: string) => void
  ): Promise<BatchUserImportResult> {
    const results: BatchUserImportResult = {
      created: 0,
      updated: 0,
      skipped: 0,
      errors: []
    };

    if (users.length === 0) {
      return results;
    }

    // Import each user
    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      const userName = user.billing_company || user.display_name || user.email;

      if (onProgress) {
        onProgress(i + 1, users.length, userName);
      }

      try {
        // Skip non-approved users (additional safety check)
        if (user.ur_status !== 1) {
          results.skipped++;
          continue;
        }

        const result = await this.importUserAsClient(user, assignedTo, statoCliente);
        if (result.action === 'created') {
          results.created++;
        } else {
          results.updated++;
        }
      } catch (error) {
        results.errors.push(`Error importing "${userName}": ${(error as Error).message}`);
      }

      // Small delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    return results;
  }

  // ========================================
  // Pending Users Management (Phase 3)
  // ========================================

  /**
   * Get ALL pending WooCommerce users (waiting for approval)
   * Calls the custom plugin endpoint /wp-json/aplwc/v1/users/pending
   */
  async getPendingUsers(): Promise<WooCommerceUsersResponse> {
    if (!this.organizationId) {
      return { success: false, data: [], total: 0, error: 'Organization ID not set' };
    }

    if (!this.config) {
      return { success: false, data: [], total: 0, error: 'WooCommerce service not initialized' };
    }

    try {
      const url = this.getCustomApiUrl('aplwc/v1/users/pending');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          data: [],
          total: 0,
          error: `WooCommerce API error (${response.status}): ${errorText}`
        };
      }

      const result = await response.json();
      return {
        success: result.success ?? true,
        data: result.data || [],
        total: result.total || 0
      };
    } catch (error) {
      console.error('Error fetching pending WooCommerce users:', error);
      return {
        success: false,
        data: [],
        total: 0,
        error: (error as Error).message
      };
    }
  }

  /**
   * Approve a pending WooCommerce user
   * Calls POST /wp-json/aplwc/v1/users/{id}/approve
   * This triggers the ur_user_status_updated hook which syncs to CRM via webhook
   *
   * @param userId - WooCommerce user ID
   * @param sendEmail - Whether to send email notification to user (default: true)
   */
  async approveUser(userId: number, sendEmail: boolean = true): Promise<UserApprovalResult> {
    if (!this.config) {
      return { success: false, error: 'WooCommerce service not initialized' };
    }

    try {
      const url = this.getCustomApiUrl(`aplwc/v1/users/${userId}/approve`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify({ send_email: sendEmail })
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `WooCommerce API error (${response.status}): ${errorText}`
        };
      }

      const result = await response.json();
      return {
        success: result.success ?? true,
        message: result.message,
        userId: result.user_id,
        status: result.status,
        emailSent: result.email_sent
      };
    } catch (error) {
      console.error('Error approving WooCommerce user:', error);
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }

  /**
   * Deny a pending WooCommerce user
   * Calls POST /wp-json/aplwc/v1/users/{id}/deny
   *
   * @param userId - WooCommerce user ID
   * @param sendEmail - Whether to send email notification to user (default: true)
   */
  async denyUser(userId: number, sendEmail: boolean = true): Promise<UserApprovalResult> {
    if (!this.config) {
      return { success: false, error: 'WooCommerce service not initialized' };
    }

    try {
      const url = this.getCustomApiUrl(`aplwc/v1/users/${userId}/deny`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify({ send_email: sendEmail })
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `WooCommerce API error (${response.status}): ${errorText}`
        };
      }

      const result = await response.json();
      return {
        success: result.success ?? true,
        message: result.message,
        userId: result.user_id,
        status: result.status,
        emailSent: result.email_sent
      };
    } catch (error) {
      console.error('Error denying WooCommerce user:', error);
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }

  /**
   * Assign a price list to a WooCommerce user
   * Calls POST /wp-json/aplwc/v1/users/{id}/assign-pricelist
   *
   * @param userId - WooCommerce user ID
   * @param priceListId - UUID of the price list to assign
   */
  async assignPriceList(userId: number, priceListId: string): Promise<UserApprovalResult> {
    if (!this.config) {
      return { success: false, error: 'WooCommerce service not initialized' };
    }

    try {
      const url = this.getCustomApiUrl(`aplwc/v1/users/${userId}/assign-pricelist`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify({ price_list_id: priceListId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        return {
          success: false,
          error: `WooCommerce API error (${response.status}): ${errorText}`
        };
      }

      const result = await response.json();
      return {
        success: result.success ?? true,
        message: result.message,
        userId: result.user_id
      };
    } catch (error) {
      console.error('Error assigning price list:', error);
      return {
        success: false,
        error: (error as Error).message
      };
    }
  }

  /**
   * @deprecated Use importSelectedUsers instead
   * Batch import all WooCommerce users from a price list
   * Only imports approved users (ur_status === 1)
   */
  async importUsersFromPriceList(
    priceListId: string,
    assignedTo: string,
    statoCliente: string,
    onProgress?: (current: number, total: number, userName: string) => void
  ): Promise<BatchUserImportResult> {
    const results: BatchUserImportResult = {
      created: 0,
      updated: 0,
      skipped: 0,
      errors: []
    };

    try {
      // Fetch users with only_approved filter
      const url = this.getCustomApiUrl(`aplwc/v1/pricelists/${priceListId}/users?only_approved=true`);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        results.errors.push(`WooCommerce API error (${response.status}): ${errorText}`);
        return results;
      }

      const usersResponse = await response.json();
      const users = usersResponse.data || [];

      if (users.length === 0) {
        return results;
      }

      // Use the new importSelectedUsers method
      return this.importSelectedUsers(users as WooCommerceUser[], assignedTo, statoCliente, onProgress);
    } catch (error) {
      results.errors.push(`General error: ${(error as Error).message}`);
    }

    return results;
  }
}

// Type for WooCommerce users (from plugin endpoint)
export interface WooCommerceUser {
  id: number;
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  billing_company: string;
  billing_phone: string;
  billing_address_1?: string;
  billing_address_2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_postcode?: string;
  billing_country?: string;
  vat_number?: string | null;
  fiscal_code?: string | null;
  pec?: string | null;
  sdi_code?: string | null;
  annual_revenue?: number | null; // Fatturato annuale (numerico)
  tipo_azienda?: string | null; // Tipologia azienda dal form WP
  fatturato?: string | null; // Fatturato come stringa dal form WP (es. "1.000.000 - 5.000.000 €")
  is_b2b: boolean;
  ur_status: number; // 1=approved, 0=pending, -1=denied
  assigned_price_list_id?: string;
  registered_date?: string;
  source: 'woocommerce';
}

// Result types for user import operations
export interface UserImportResult {
  clientId: string;
  action: 'created' | 'updated';
}

export interface BatchUserImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export interface WooCommerceUsersResponse {
  success: boolean;
  data: WooCommerceUser[];
  total: number;
  error?: string;
}

// Result type for user approval/deny operations
export interface UserApprovalResult {
  success: boolean;
  message?: string;
  userId?: number;
  status?: 'approved' | 'denied';
  emailSent?: boolean;
  error?: string;
}

// Type for order records from database
export interface WooCommerceOrderRecord {
  id: string;
  organization_id: string;
  woo_order_id: number;
  order_number: string;
  status: string;
  total: number;
  currency: string;
  customer_email: string;
  customer_name: string;
  client_id: string | null;
  order_data: any;
  internal_notes?: string | null;
  notes_updated_at?: string | null;
  created_at: string;
  updated_at: string;
  client?: {
    id: string;
    business_name: string;
    email: string;
    vat_number: string;
    phone?: string;
    street_address?: string;
    city?: string;
    postal_code?: string;
    province?: string;
  };
}

export const woocommerceService = new WooCommerceService();